import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { DepotDto } from "@gatiq/shared";
import { Pencil, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Page,
  PageHeader,
  SearchInput,
  SlideOver,
  TD,
  TH,
  THead,
  RowActions,
  TR,
  Table,
  TableSkeleton,
  TextInput,
  Toolbar,
  ToolbarCount,
  statusTone,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { matchesTableSearch } from "@/lib/tableSearch";
import { useCan } from "@/auth/access";
import {
  AddressFields,
  EMPTY_ADDRESS,
  FormSection,
  type AddressFormState,
} from "@/components/AddressFields";



interface DepotFormState extends AddressFormState {
  name: string;
  phone: string;
  email: string;
}

const EMPTY_DEPOT_FORM: DepotFormState = { ...EMPTY_ADDRESS, name: "", phone: "", email: "" };

/**
 * Every field, trimmed — blanks included.
 *
 * Dropping the empty ones would mean an optional field could be filled but
 * never emptied again: the update skips whatever key it is not sent.
 */
function depotPayload(f: DepotFormState): Record<string, string> {
  return Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v.trim()]));
}

export function DepotsPage() {
  const toast = useToast();
  const can = useCan();
  const canCreate = can("depots", "create");
  const canEdit = can("depots", "edit");
  const canDelete = can("depots", "delete");
  const [depots, setDepots] = useState<DepotDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  // Create modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<DepotFormState>(EMPTY_DEPOT_FORM);
  const [savingCreate, setSavingCreate] = useState(false);
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});

  // Edit / detail state
  const [selected, setSelected] = useState<DepotDto | null>(null);
  const [editForm, setEditForm] = useState<DepotFormState>(EMPTY_DEPOT_FORM);
  const [editStatus, setEditStatus] = useState<"active" | "inactive">("active");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});

  // Delete state
  const [depotToDelete, setDepotToDelete] = useState<DepotDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadDepots = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<DepotDto[] | { items: DepotDto[]; total: number }>("/org/depots");
      const items = Array.isArray(res) ? res : res.items || [];
      setDepots(items);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load depots.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadDepots();
  }, [loadDepots]);

  /** Apply a field change and clear only the errors for the fields it touched. */
  const patchCreate = useCallback((patch: Partial<DepotFormState>): void => {
    setCreateForm((f) => ({ ...f, ...patch }));
    setCreateErrors((errs) => {
      const next = { ...errs };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
  }, []);

  const patchEdit = useCallback((patch: Partial<DepotFormState>): void => {
    setEditForm((f) => ({ ...f, ...patch }));
    setEditErrors((errs) => {
      const next = { ...errs };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
  }, []);

  function validateCreate(): boolean {
    const errors: Record<string, string> = {};
    if (!createForm.name.trim()) errors.name = "Enter depot name.";
    if (!createForm.phone.trim()) errors.phone = "Enter a mobile number.";
    if (!createForm.state.trim()) errors.state = "Choose a state.";
    if (!createForm.city.trim()) errors.city = "Enter a city or town.";
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!validateCreate()) return;

    setSavingCreate(true);
    try {
      // The code is derived server-side from the org and the town.
      await api.post<DepotDto>("/org/depots", depotPayload(createForm));
      toast.success(`Depot ${createForm.name} created.`);
      setCreateOpen(false);
      setCreateForm(EMPTY_DEPOT_FORM);
      setCreateErrors({});
      await loadDepots();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.fields) setCreateErrors(err.fields);
        toast.error(err.message);
      } else {
        toast.error("Could not create depot.");
      }
    } finally {
      setSavingCreate(false);
    }
  }

  function openEdit(depot: DepotDto): void {
    setSelected(depot);
    setEditForm({
      name: depot.name,
      phone: depot.phone || "",
      email: depot.email || "",
      state: depot.state || "",
      city: depot.city || "",
      district: depot.district || "",
      pincode: depot.pincode || "",
      village: depot.village || "",
      address: depot.address || "",
      gstNumber: depot.gstNumber || "",
    });
    setEditStatus(depot.status);
    setEditErrors({});
  }

  async function handleSaveEdit(): Promise<void> {
    if (!selected) return;
    const errors: Record<string, string> = {};
    if (!editForm.name.trim()) errors.name = "Depot name is required.";
    if (!editForm.phone.trim()) errors.phone = "A mobile number is required.";
    if (!editForm.state.trim()) errors.state = "State is required.";
    if (!editForm.city.trim()) errors.city = "City is required.";
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      return;
    }

    setSavingEdit(true);
    try {
      await api.patch<DepotDto>(`/org/depots/${selected.id}`, {
        ...depotPayload(editForm),
        status: editStatus,
      });
      toast.success("Depot updated successfully.");
      setSelected(null);
      await loadDepots();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.fields) setEditErrors(err.fields);
        toast.error(err.message);
      } else {
        toast.error("Could not update depot.");
      }
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!depotToDelete) return;
    setDeleting(true);
    try {
      await api.del(`/org/depots/${depotToDelete.id}`);
      toast.success(`Depot ${depotToDelete.name} deleted.`);
      setDepotToDelete(null);
      if (selected?.id === depotToDelete.id) setSelected(null);
      await loadDepots();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not delete depot.");
    } finally {
      setDeleting(false);
    }
  }

  const filteredDepots = depots.filter((depot) => matchesTableSearch(q, depot));

  return (
    <Page bleed>
      <PageHeader
        title="Regional Depots"
      />

      <Card fill>
        <Toolbar
          trailing={
            <>
              {filteredDepots.length > 0 ? <ToolbarCount count={filteredDepots.length} noun="depot" /> : null}
              {canCreate ? <Button onClick={() => setCreateOpen(true)}>+ Add depot</Button> : null}
            </>
          }
        >
          <SearchInput
            label="Search depots"
            onChange={(val) => setQ(val)}
            placeholder="Search by name, code, state, city…"
            value={q}
          />
        </Toolbar>

        {loading ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TableSkeleton cols={6} />
          </div>
        ) : filteredDepots.length === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <EmptyState
              action={canCreate ? <Button onClick={() => setCreateOpen(true)}>+ Add Depot</Button> : undefined}
              message={q ? "No depots matching your search." : "Add a regional depot to manage warehouse stock routes."}
              title="No depots found"
            />
          </div>
        ) : (
          <Table fill searchable={false}>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Code</TH>
                <TH>State</TH>
                <TH>City</TH>
                <TH>Status</TH>
                <TH align="right" width="4.5rem">
                  <span className="sr-only">Actions</span>
                </TH>
              </TR>
            </THead>
            <tbody>
              {filteredDepots.map((depot) => (
                <TR key={depot.id} onClick={canEdit ? () => openEdit(depot) : undefined}>
                  <TD>
                    <span className="font-medium text-ink">{depot.name}</span>
                    {depot.address ? (
                      <span className="block truncate text-xs text-muted max-w-xs">{depot.address}</span>
                    ) : null}
                  </TD>
                  <TD>
                    <span className="inline-block rounded border border-rule bg-surface px-2 py-0.5 font-mono text-xs font-semibold text-ink">
                      {depot.code}
                    </span>
                  </TD>
                  <TD>{depot.state || "—"}</TD>
                  <TD>{depot.city || "—"}</TD>
                  <TD>
                    <Badge tone={statusTone(depot.status)}>
                      {depot.status === "active" ? "Active" : "Inactive"}
                    </Badge>
                  </TD>
                  <TD align="right">
                    <RowActions
                      items={[
                        { label: "Edit", icon: Pencil, hidden: !canEdit, onSelect: () => openEdit(depot) },
                        {
                          label: "Delete",
                          icon: Trash2,
                          danger: true,
                          hidden: !canDelete,
                          onSelect: () => setDepotToDelete(depot),
                        },
                      ]}
                      label={depot.name}
                    />
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Add Depot SlideOver */}
      <SlideOver
        footer={
          <>
            <Button onClick={() => setCreateOpen(false)} variant="secondary">
              Cancel
            </Button>
            <Button form="create-depot-form" loading={savingCreate} type="submit">
              Create Depot
            </Button>
          </>
        }
        onClose={() => setCreateOpen(false)}
        open={createOpen}
        title="Add Regional Depot"
      >
        <form className="flex flex-col gap-4" id="create-depot-form" noValidate onSubmit={handleCreate}>
          <FormSection title="Depot">
            <TextInput
              error={createErrors.name}
              label="Depot name"
              onChange={(e) => patchCreate({ name: e.target.value })}
              placeholder="e.g. Rajasthan Central Depot"
              required
              value={createForm.name}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextInput
                error={createErrors.phone}
                label="Mobile number"
                onChange={(e) => patchCreate({ phone: e.target.value })}
                placeholder="98765 43210"
                required
                value={createForm.phone}
              />
              <TextInput
                error={createErrors.email}
                hint="Optional"
                label="Email"
                onChange={(e) => patchCreate({ email: e.target.value })}
                placeholder="depot@company.com"
                type="email"
                value={createForm.email}
              />
            </div>
            <p className="text-xs text-muted">
              The depot code is generated from your organization and the town when you save.
            </p>
          </FormSection>

          <FormSection
            description="District and PIN fill themselves from the state and town."
            title="Location"
          >
            <AddressFields
              errors={createErrors}
              onChange={patchCreate}
              required
              value={createForm}
            />
          </FormSection>
        </form>
      </SlideOver>

      {/* Edit Depot SlideOver */}
      <SlideOver
        footer={
          selected ? (
            <>
              {canDelete ? (
                <Button onClick={() => setDepotToDelete(selected)} variant="danger">
                  Delete
                </Button>
              ) : null}
              <Button loading={savingEdit} onClick={() => void handleSaveEdit()}>
                Save Changes
              </Button>
            </>
          ) : null
        }
        onClose={() => setSelected(null)}
        open={Boolean(selected)}
        title={`Edit ${selected?.name ?? "Depot"}`}
      >
        {selected ? (
          <div className="flex flex-col gap-4">
            {/* Read-only: the code is printed on dispatch paperwork, so
                rewriting it later would strand older documents. */}
            <div className="rounded-md border border-rule bg-surface px-3 py-2">
              <p className="text-xs text-muted">Depot code</p>
              <p className="font-mono text-sm text-ink">{selected.code}</p>
            </div>
            <FormSection title="Depot">
              <TextInput
                error={editErrors.name}
                label="Depot name"
                onChange={(e) => patchEdit({ name: e.target.value })}
                required
                value={editForm.name}
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextInput
                  error={editErrors.phone}
                  label="Mobile number"
                  onChange={(e) => patchEdit({ phone: e.target.value })}
                  required
                  value={editForm.phone}
                />
                <TextInput
                  error={editErrors.email}
                  hint="Optional"
                  label="Email"
                  onChange={(e) => patchEdit({ email: e.target.value })}
                  type="email"
                  value={editForm.email}
                />
              </div>
            </FormSection>

            <FormSection title="Location">
              <AddressFields errors={editErrors} onChange={patchEdit} required value={editForm} />
            </FormSection>
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Status</label>
              <div className="flex items-center gap-4">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                  <input
                    checked={editStatus === "active"}
                    className="accent-[var(--gq-color-1)]"
                    name="depot-status"
                    onChange={() => setEditStatus("active")}
                    type="radio"
                  />
                  Active
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                  <input
                    checked={editStatus === "inactive"}
                    className="accent-[var(--gq-color-1)]"
                    name="depot-status"
                    onChange={() => setEditStatus("inactive")}
                    type="radio"
                  />
                  Inactive
                </label>
              </div>
            </div>
          </div>
        ) : null}
      </SlideOver>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        loading={deleting}
        message={`Delete depot "${depotToDelete?.name ?? ""}" (${depotToDelete?.code ?? ""})? This cannot be undone.`}
        onClose={() => setDepotToDelete(null)}
        onConfirm={() => void handleDelete()}
        open={Boolean(depotToDelete)}
        title="Delete Depot"
      />
    </Page>
  );
}
